import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import { Palette, Check } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import WindowContainer from "../../components/layout/WindowContainer";
import { NeonCard, NeonCardContent, NeonCardHeader } from "../../NeonComponents/NeonCards";
import { NeonGlassItem } from "../../NeonComponents/NeonGlassItem";
import { NeonIconBox } from "../../NeonComponents/NeonIconBox";
import { getNeonColor } from "../../theme";
import { useThemeContext } from "../../themeContext";
import { NUMBER_SCALE_PALETTES, NumberScalePalette, SCALE_STOPS, scaleColor, scaleGlowSx } from "../../settings/numberScale";
import formatters from "../../NeonComponents/Formatters";

const PREVIEW = [
    { label: "Low", value: 0.32, stops: SCALE_STOPS.rate, format: (v: number) => formatters.percent(v) },
    { label: "ACS", value: 188, stops: SCALE_STOPS.acs, format: (v: number) => formatters.integer(v) },
    { label: "KD", value: 1.08, stops: SCALE_STOPS.kd, format: (v: number) => formatters.float2(v) },
    { label: "Rating", value: 1.19, stops: SCALE_STOPS.rating, format: (v: number) => formatters.float2(v) },
    { label: "High", value: 0.68, stops: SCALE_STOPS.rate, format: (v: number) => formatters.percent(v) },
];

function PalettePreview({ palette }: { palette: NumberScalePalette }) {
    return (
        <Stack direction="row" spacing={1.25} sx={{ alignItems: "flex-end", flexWrap: "wrap" }}>
            {PREVIEW.map((item) => {
                const color = scaleColor(item.value, item.stops, palette);
                return (
                    <Box key={item.label} sx={{ minWidth: 52, textAlign: "center" }}>
                        <Typography sx={{ fontSize: "0.58rem", letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.4)", mb: 0.4 }}>
                            {item.label}
                        </Typography>
                        <Typography sx={{ fontWeight: 800, fontSize: "0.92rem", ...scaleGlowSx(color, palette.glow) }}>
                            {item.format(item.value)}
                        </Typography>
                    </Box>
                );
            })}
        </Stack>
    );
}

function Swatches({ colors }: { colors: string[] }) {
    return (
        <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
            {colors.map((color) => (
                <Box
                    key={color}
                    sx={{
                        width: 18,
                        height: 18,
                        borderRadius: "4px",
                        backgroundColor: color,
                        boxShadow: `0 0 8px ${color}55`,
                        border: "1px solid rgba(255,255,255,0.18)",
                    }}
                />
            ))}
        </Stack>
    );
}

export default function SettingsPage() {
    const theme = useTheme();
    const neonColor = getNeonColor(theme);
    const { numberScaleId, setNumberScaleId } = useThemeContext();

    return (
        <WindowContainer spacing={2} sx={{ height: "100%", minHeight: 0, overflow: "auto", pb: 3 }}>
            <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", flexShrink: 0 }}>
                <NeonIconBox size={48}>
                    <Palette />
                </NeonIconBox>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: 0.3 }}>
                        Settings
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        Appearance for stats and number scales
                    </Typography>
                </Box>
            </Stack>

            <NeonCard>
                <NeonCardHeader title="Number scale" sx={{ minHeight: "auto", py: 1.25 }} />
                <NeonCardContent sx={{ pt: 0, display: "flex", flexDirection: "column", gap: 1.5 }}>
                    <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: "0.85rem", mb: 0.5 }}>
                        Pick how performance numbers are colored. This applies to SoloQ breakdowns and match ratings.
                    </Typography>
                    {NUMBER_SCALE_PALETTES.map((palette) => {
                        const selected = palette.id === numberScaleId;
                        return (
                            <NeonGlassItem
                                key={palette.id}
                                isClickable
                                onClick={() => setNumberScaleId(palette.id)}
                                sx={{
                                    p: 1.75,
                                    border: selected ? `1px solid ${neonColor}80` : "1px solid rgba(255,255,255,0.1)",
                                    boxShadow: selected ? `0 0 18px ${neonColor}22, inset 0 0 12px ${neonColor}10` : undefined,
                                    background: selected ? `${neonColor}10` : "transparent",
                                }}
                            >
                                <Stack
                                    direction={{ xs: "column", md: "row" }}
                                    spacing={2}
                                    sx={{ alignItems: { xs: "stretch", md: "center" }, justifyContent: "space-between" }}
                                >
                                    <Stack spacing={0.6} sx={{ minWidth: 220, flexShrink: 0 }}>
                                        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                                            <Typography sx={{ fontWeight: 800, fontSize: "0.95rem" }}>{palette.label}</Typography>
                                            {selected ? (
                                                <Stack direction="row" spacing={0.4} sx={{ alignItems: "center", color: neonColor }}>
                                                    <Check sx={{ fontSize: 16 }} />
                                                    <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>
                                                        Active
                                                    </Typography>
                                                </Stack>
                                            ) : null}
                                        </Stack>
                                        <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.5)" }}>{palette.description}</Typography>
                                        <Swatches colors={palette.colors} />
                                    </Stack>
                                    <PalettePreview palette={palette} />
                                </Stack>
                            </NeonGlassItem>
                        );
                    })}
                </NeonCardContent>
            </NeonCard>
        </WindowContainer>
    );
}
